import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { appointmentService } from '../../../services/appointmentService';
import { supabase } from '../../../lib/supabase';
import { Lock, LockKeyholeOpen } from 'lucide-react';
import { useAvailableTimeSlots } from 'hooks'; 

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
const today = new Date()?.toISOString()?.split('T')?.[0];

const timeToMinutes = (time) => {
    const [hours, minutes] = time?.split(':')?.map(Number);
    return hours * 60 + minutes;
  };

const BlockSlotsModal = ({ 
  isOpen, 
  onClose,
  onSuccess,
  psychologistProfile,
  currentDate
}) => {
  
const {
  availableTimeSlots,
  loadingAvailableTimeSlots,
  errorAvailableTimeSlots,
  getAvailableTimeSlots,
} = useAvailableTimeSlots();

const [isSubmitting, setIsSubmitting] = useState(false);
const [errors, setErrors] = useState({});

  // Load available time slots
  useEffect(() => {
    const loadAvailableTimeSlots = async () => {
      if (!currentDate || !psychologistProfile) {
        return;
      }

      try {
        await getAvailableTimeSlots(currentDate, psychologistProfile.id, psychologistProfile.work_lunch_interval);
      } catch (err) {
        console.error('Error loading available time slots:', err);
        setErrors('Erro ao carregar horários disponíveis');
      }
    };

    loadAvailableTimeSlots();
  
  }, [currentDate, psychologistProfile.id, psychologistProfile.work_lunch_interval, getAvailableTimeSlots]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  // ESC key handler to close modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event?.key === 'Escape' && isOpen && !isSubmitting) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, isSubmitting]);

  // NEW: Generate available time slots when date changes
  useEffect(() => {
    if (currentDate && psychologistProfile) {

      // Formata a data como YYYY-MM-DD (exatamente como está no banco)
      const dateStr = new Date(currentDate).toISOString().split('T')[0];

      // Nome único do canal por data + psicólogo (evita conflito)
      const channelName = `appointments-${psychologistProfile.id}-${dateStr}`;

      const channel = supabase
        .channel(channelName)
        .on('postgres_changes', 
          { event: '*', 
            schema: 'public', 
            table: 'appointments', 
            filter: `psychologist_id=eq.${psychologistProfile.id}`
          }, 
          (payload) => {

          getAvailableTimeSlots(currentDate, psychologistProfile.id, psychologistProfile.work_lunch_interval || null);

          // Faz atualizar os dados da tabela da página pai.
          onSuccess();

      }) 
      .subscribe((status) => {
      });

      // Carrega uma vez, na montagem.
      getAvailableTimeSlots(currentDate, psychologistProfile.id, psychologistProfile.work_lunch_interval || null);

      // Faz atualizar os dados da tabela da página pai.
      onSuccess();
      
      // Cleanup na troca de data ou desmontagem
        return () => {
          supabase.removeChannel(channel);
        };
        
    }
  }, [currentDate, psychologistProfile]);

useEffect(() => {
  if (!psychologistProfile) {
    return;
  }

  //console.log('🔌 Setting up real-time subscription for psychologist:', psychologistProfile.id);
  
  const channelName = `temp-appointments-psych-${psychologistProfile.id}-${Math.floor(Math.random() * 1000000)}`;

  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: '*', // Listen to INSERT, UPDATE, DELETE
        schema: 'public',
        table: 'temp_public_patients',
      },
      (payload) => {
        //console.log('🔄 Real-time appointment change detected:', payload);
        
          // Reload available slots automatically
          if (currentDate) {
            getAvailableTimeSlots(currentDate, psychologistProfile.id, psychologistProfile.work_lunch_interval || null);
          }            
      }
    ).subscribe((status) => {
      //console.log('📡 Subscription status:', status);
      
      if (status === 'SUBSCRIBED') {
        //console.log('✅ Successfully subscribed to real-time updates');
      } else if (status === 'CHANNEL_ERROR') {
        console.error('❌ Channel subscription error');
      }
    });

    // Carrega os slots iniciais (só quando selectedDate existe)
    if (currentDate) {
      getAvailableTimeSlots(currentDate, psychologistProfile.id, psychologistProfile.work_lunch_interval || null);
    }

  // Cleanup subscription when component unmounts or dependencies change
  return () => {
    //console.log('🔌 Cleaning up real-time subscription');
    supabase.removeChannel(channel);
  };
}, [psychologistProfile, currentDate]); // Re-subscribe when date changes to update the filter logic




  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const handleTimeSelect = async (slot) => {
    if (slot.value) {
      
      // Cria um novo agendamento com um status "Blocked" (ou apaga, caso exista).
      try {

        let dados, error;

        const dateStr = new Date(currentDate).toISOString().split('T')[0];
        const [hours, minutes] = slot.value.split(':').map(Number);

        const baseDate = new Date();
        baseDate.setHours(hours);
        baseDate.setMinutes(minutes + 30); // acrescenta 30 minutos

        const endTimeHours = String(baseDate.getHours()).padStart(2, '0');
        const endTimeMinutes = String(baseDate.getMinutes()).padStart(2, '0');
        const endTime = `${endTimeHours}:${endTimeMinutes}`;

        // Verifica se já existe o registro do bloqueio. Em caso afirmativo, apaga e retorna.
        ({ data: dados, error } = await supabase
          ?.from('appointments')
          ?.select('id, appointment_date, start_time, end_time, psychologist_id, status')
          ?.eq('psychologist_id', psychologistProfile.id)
          ?.eq('appointment_date', dateStr)
          ?.eq('start_time', slot.value)
          ?.eq('end_time', endTime)
          ?.eq('status', 'blocked')
          ?.limit(1)
          ?.maybeSingle());

          if (error) {
            console.error('Erro ao verificar existência de horário bloqueado : ', error);
             throw error;
          }

          if(dados)
          {
             // Encontrou o registro. Então, apaga e sai.
             const { error: deleteError } = await appointmentService?.deleteAppointment(dados.id);

             if (deleteError) throw deleteError;      

             // Se tudo deu certo, atualiza a tela e retorna.
             await getAvailableTimeSlots(currentDate, psychologistProfile.id, psychologistProfile.work_lunch_interval || null);

             // Faz atualizar os dados da tabela da página pai.
             onSuccess();

             return true;
          }

      const appointmentData = {
          psychologist_id: psychologistProfile.id,
          patient_id: null,
          title: 'Horário Bloqueado',
          appointment_date: dateStr,
          start_time: slot.value,
          end_time: endTime,
          duration: 50,
          appointment_type: 'therapy_session',
          status: 'blocked'
        };

        const { data, error: createError } = await appointmentService?.createAppointment({
          ...appointmentData,
          psychologist_id: psychologistProfile.id
        });

        if (createError) throw createError;

        // Faz atualizar os dados da tabela da página pai.
        onSuccess();
        
        return { success: true };
      } catch (err) {
        console.error('Error creating "Blocked" appointment:', err);
        return { success: false, error: err?.message };
      }      
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
        onClick={handleClose}
      >
         {/* Container principal do modal */}
        <div 
          className="bg-card rounded-xl shadow-2xl border border-border w-full max-w-2xl max-h-[95vh] flex flex-col overflow-hidden mx-auto my-auto"
          style={{ minHeight: '90vh', maxHeight: '96vh' }}
          onClick={(e) => e?.stopPropagation()}
        >

          {/* Header */}
          <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10 shadow-sm">
            <h2 className="text-xl font-semibold text-foreground">
              Bloqueio de Horários
            </h2>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-lg hover:bg-muted transition-smooth flex items-center justify-center"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* CORPO DO MODAL – AQUI VAI TODO O CONTEÚDO AQUI DENTRO */}
          <div className='px-10 py-5'>
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Clock" size={20} color="var(--color-primary)" />
              Selecione para alterar
            </h3>

            {loadingAvailableTimeSlots ? (
              <div className="text-center py-12">
                <Icon 
                  name="Loader" 
                  size={40} 
                  color="var(--color-primary)" 
                  className="animate-spin mx-auto mb-4" 
                />
                <p className="text-muted-foreground">Carregando horários disponíveis...</p>
              </div>
            ) : availableTimeSlots?.length > 0 ? (
              <>
                <div className="grid grid-cols-4 gap-3 mb-4">
                  {availableTimeSlots?.map((slot, index) => (
                    <button
                      key={slot.id}
                      type="button"
                      title={slot.isBlocked ? 'Desbloquear' : 'Bloquear'}
                      onClick={() => handleTimeSelect(slot)}
                      className={`group rounded-lg font-medium transition-all flex items-center justify-center gap-2 px-3 py-4 min-w-0
                          ${ !slot.isBlocked ? 'bg-muted hover:bg-muted/80 text-foreground hover:scale-105'
                            : 'bg-gray-300 text-[#969696]'}
                      `}
                    >
                    <span className={
                                      `${!isMobile && slot.isBlocked ? 'relative w-3.5 h-3.5 -ml-1 -mt-1.5 pl-2' : '-ml-6 w-3.5 h-8.0 pt-0 mb-2'}`
                                      }>{slot?.label}</span>
                    {slot?.isBlocked && 
                      <div className={` ${!isMobile && slot.isBlocked ? 'relative w-3.5 h-3.5 ml-8' : 'absolute w-3.5 h-3.5 ml-1 mt-8' }`}>
                        <Lock className="w-3.5 h-3.5 text-[#969696] absolute inset-0 transition-opacity duration-200 group-hover:opacity-0" />
                        <LockKeyholeOpen className="w-3.5 h-3.5 text-[#969696] absolute inset-0 transition-opacity duration-200 opacity-0 group-hover:opacity-100" />
                      </div>
                    }
                    </button>
                  ))}
                </div>

              </>

              ) : (
                <div className="text-center py-12 bg-muted/50 rounded-lg">
                  <Icon
                    name="CalendarX"
                    size={48}
                    color="var(--color-muted-foreground)"
                    className="mx-auto mb-4 opacity-50"
                  />
                  <p className="text-muted-foreground text-lg font-medium mb-2">
                    Nenhum horário disponível
                  </p>
                  <p className="text-muted-foreground text-sm">
                    Tente selecionar outra data
                  </p>
                </div>
              )}
            </div>
        </div>
        </div>
        
    </>
  );
};

export default BlockSlotsModal;